import React, { useEffect, useState, useContext } from "react";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIMenu from "@mui/material/Menu";
import MUIChip from "@mui/material/Chip";
import MUIBox from "@mui/material/Box";
import MUITypography from "@mui/material/Typography";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Widget from "../../Widget";
import Setting from "../../../Utility/Setting";
import Format from "../../../Utility/Format";

/**
 * Sums out of bounds counts and total order counts across all queues in the
 * widget data response.
 *
 * @param {object|null} widgetData Loaded widget response data.
 * @returns {{ totalOutOfBoundsCount: number, totalOrderCount: number }}
 */
function getOutOfBoundsTotals(widgetData) {
  let totalOutOfBoundsCount = 0;
  let totalOrderCount = 0;

  if (widgetData && widgetData.queues) {
    Object.values(widgetData.queues).forEach((queueData) => {
      totalOutOfBoundsCount += queueData.outOfBounds || 0;
      totalOrderCount += queueData.count || 0;
    });
  }

  return {
    totalOutOfBoundsCount: totalOutOfBoundsCount,
    totalOrderCount: totalOrderCount,
  };
}

/**
 * Renders the out of bounds count and synchronizes the totals up to the parent
 * widget so the subtitle can display the percentage.
 *
 * @param {object} props
 * @param {object|null} props.widgetData Loaded widget response data.
 * @param {function} props.onOutOfBoundsSubtitleValuesChange Pushes the latest
 * totals (out of bounds count and total order count) up to the parent for use
 * in the subtitle.
 * @returns {React.ReactElement}
 */
function WidgetDashboardOOBContent({
  widgetData,
  onOutOfBoundsSubtitleValuesChange,
}) {
  const { totalOutOfBoundsCount, totalOrderCount } =
    getOutOfBoundsTotals(widgetData);

  useEffect(() => {
    onOutOfBoundsSubtitleValuesChange({
      totalOutOfBoundsCount: totalOutOfBoundsCount,
      totalOrderCount: totalOrderCount,
    });
  }, [
    onOutOfBoundsSubtitleValuesChange,
    totalOutOfBoundsCount,
    totalOrderCount,
  ]);

  return (
    <>
      {totalOutOfBoundsCount === 0 ? (
        <MUITypography component="div" variant="h5" color="success.main">
          0
        </MUITypography>
      ) : (
        <MUITypography component="div" variant="h5" color="error">
          {widgetData && widgetData.queues
            ? Format.number(totalOutOfBoundsCount)
            : "—"}
        </MUITypography>
      )}
    </>
  );
}

/**
 * Renders the queue selection chip and dropdown menu in the widget visual slot.
 * Owns the menu anchor state locally because nothing outside the visual needs
 * to read or react to it.
 *
 * @param {object} props
 * @param {string} props.widgetId Identifier of the widget.
 * @param {object|null} props.widgetData Loaded widget response data.
 * @param {string[]} props.configuredQueueIds Queue identifiers from the widget configuration.
 * @param {object} props.queuesIndexed Queue records indexed by queue identifier (from MainContext).
 * @param {string|null} props.selectedQueueId Identifier of the currently selected queue.
 * @param {function} props.onSelectedQueueIdChange Setter that records the newly selected queue identifier.
 * @param {function} props.onUpdateActiveWidgetConfiguration Pushes the new dashboard configuration up to the parent widget.
 * @param {function} [props.onActiveWidgetChange] Optional callback invoked after the active widget configuration changes.
 * @returns {React.ReactElement|null}
 */
function WidgetDashboardOOBVisual({
  widgetId,
  widgetData,
  configuredQueueIds,
  queuesIndexed,
  selectedQueueId,
  onSelectedQueueIdChange,
  onUpdateActiveWidgetConfiguration,
  onActiveWidgetChange,
}) {
  const [queueMenuAnchorElement, setQueueMenuAnchorElement] = useState(null);
  const isQueueMenuOpen = Boolean(queueMenuAnchorElement);

  const handleQueueMenuOpen = (clickEvent) => {
    setQueueMenuAnchorElement(clickEvent.currentTarget);
  };

  const handleQueueMenuClose = () => {
    setQueueMenuAnchorElement(null);
  };

  // Build queue options with per-queue out of bounds percentages.
  // Queues whose percentage is zero (or that have no data) are excluded from the dropdown.
  const queueOptionsWithPercentages = configuredQueueIds
    .map((queueId) => {
      const queueData =
        widgetData && widgetData.queues ? widgetData.queues[queueId] : null;
      const queueOutOfBoundsCount =
        queueData && queueData.outOfBounds ? queueData.outOfBounds : 0;

      return {
        key: queueId,
        value: queueId,
        label: queuesIndexed[queueId].name,
        outOfBoundsCount: queueOutOfBoundsCount,
      };
    })
    .filter((queueOption) => queueOption.outOfBoundsCount > 0);

  /**
   * Handle a queue being selected from the menu. Updates local state,
   * persists the selection, and notifies the parent widget.
   *
   * @param {string} newQueueId Identifier of the queue chosen by the user.
   */
  const handleQueueSelection = (newQueueId) => {
    onSelectedQueueIdChange(newQueueId);
    Setting.set(`dashboard.${widgetId}.selectedQueueId`, newQueueId);
    onUpdateActiveWidgetConfiguration({
      dashboardConfiguration: {
        queueId: newQueueId,
        viewOutOfBoundsOnly: true,
      },
    });
    onActiveWidgetChange();
    handleQueueMenuClose();
  };

  return (
    <MUIBox sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
      <MUIChip
        color="primary"
        label="Queue"
        clickable={true}
        onClick={handleQueueMenuOpen}
        onDelete={handleQueueMenuOpen}
        deleteIcon={<MUIExpandMoreIcon />}
      />
      <MUIMenu
        id="queue-selection-menu"
        anchorEl={queueMenuAnchorElement}
        open={isQueueMenuOpen}
        onClose={handleQueueMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {queueOptionsWithPercentages.length === 0 && (
          <MUIMenuItem disabled>
            <MUITypography variant="body2" color="text.secondary">
              No queues with out of bounds orders
            </MUITypography>
          </MUIMenuItem>
        )}
        {queueOptionsWithPercentages.map((queueOption) => (
          <MUIMenuItem
            key={queueOption.key}
            selected={queueOption.value === selectedQueueId}
            onClick={() => handleQueueSelection(queueOption.value)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span>{queueOption.label}</span>
            <MUIBox sx={{ marginLeft: "auto", minWidth: "40px" }}>
              <MUIChip
                label={Format.number(queueOption.outOfBoundsCount)}
                size="small"
                color="error"
                variant="contained"
                sx={{ width: "100%", height: "18px", fontSize: "0.75rem" }}
              />
            </MUIBox>
          </MUIMenuItem>
        ))}
      </MUIMenu>
    </MUIBox>
  );
}

/**
 * Sales queue dashboard widget: Out of Box (OOB) orders for the current filters.
 *
 * @param {object} props
 * @param {object} props.queue Current queue record from context.
 * @param {array} props.prefilters Prefilter objects from the queue screen (same shape as `getQueueRows`).
 * @param {function} props.onReloadQueueRows Reloads queue rows and statistics after refresh.
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardOOB(props) {
  const mainContext = useContext(MainContext);
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [configuredQueueIds, setConfiguredQueueIds] = useState([]);
  const [outOfBoundsSubtitleValues, setOutOfBoundsSubtitleValues] = useState({
    totalOutOfBoundsCount: 0,
    totalOrderCount: 0,
  });

  /**
   * Set the configured queue identifiers based on the dashboard widget configuration.
   */
  useEffect(() => {
    if (props.dashboardWidget && props.dashboardWidget.jsonConfiguration) {
      const queueIdsFromConfiguration =
        JSON.parse(props.dashboardWidget.jsonConfiguration).queue_ids || [];
      setConfiguredQueueIds(queueIdsFromConfiguration);
    }
  }, [props.dashboardWidget, selectedQueueId]);

  /**
   * Update the active widget configuration when the widget is activated.
   */
  useEffect(() => {
    if (
      props.isActive &&
      Setting.get(`dashboard.${props.widgetId}.selectedQueueId`) !== null
    ) {
      props.updateActiveWidgetConfiguration({
        dashboardConfiguration: {
          queueId: Setting.get(`dashboard.${props.widgetId}.selectedQueueId`),
          viewOutOfBoundsOnly: true,
        },
      });
    } else {
      Setting.clear(`dashboard.${props.widgetId}.selectedQueueId`);
    }
  }, [props.isActive, selectedQueueId]);

  return (
    <Widget
      getWidgetId={() => props.widgetId}
      getTitle={() => props.dashboardWidget.name}
      isActive={() => props.isActive}
      getSubtitle={() => {
        if (outOfBoundsSubtitleValues.totalOrderCount <= 0) {
          return null;
        }
        const outOfBoundsPercentageRaw =
          (outOfBoundsSubtitleValues.totalOutOfBoundsCount /
            outOfBoundsSubtitleValues.totalOrderCount) *
          100;
        const outOfBoundsPercentageFixed = outOfBoundsPercentageRaw.toFixed(1);
        const outOfBoundsPercentageDisplay =
          outOfBoundsPercentageFixed.endsWith(".0")
            ? Math.round(outOfBoundsPercentageRaw).toString()
            : outOfBoundsPercentageFixed;
        return `${outOfBoundsPercentageDisplay}% out of ${Format.number(outOfBoundsSubtitleValues.totalOrderCount)}`;
      }}
      renderVisual={({ widgetData }) => (
        <WidgetDashboardOOBVisual
          widgetId={props.widgetId}
          widgetData={widgetData}
          configuredQueueIds={configuredQueueIds}
          queuesIndexed={mainContext.queuesIndexed}
          selectedQueueId={selectedQueueId}
          onSelectedQueueIdChange={setSelectedQueueId}
          onUpdateActiveWidgetConfiguration={
            props.updateActiveWidgetConfiguration
          }
          onActiveWidgetChange={props.onActiveWidgetChange}
        />
      )}
      renderContent={({ widgetData }) => (
        <WidgetDashboardOOBContent
          widgetData={widgetData}
          onOutOfBoundsSubtitleValuesChange={setOutOfBoundsSubtitleValues}
        />
      )}
    />
  );
}
